import { VALID_MESSAGE_TYPES } from "cortex/ui/src/constant/protobuf";
import { captureStack } from "cortex/ui/src/integrations/sentry";
import { PayloadType } from "cortex/ui/static/@cortex/messages/enums/payload_type_pb";
import { Envelope } from "cortex/ui/static/@cortex/messages/schemas/envelope_pb";
import { Message } from "cortex/ui/static/@cortex/messages/schemas/message_pb";
import { Progress } from "cortex/ui/static/@cortex/messages/schemas/progress_pb";
import { TaskResult } from "cortex/ui/static/@cortex/messages/schemas/task_result_pb";

import * as Sentry from "@sentry/browser";

interface BinaryPayloadLike {
  value: Uint8Array;
}

interface EnvelopeLike {
  payload: BinaryPayloadLike;
  type: number;
}

interface MessageLike {
  level: number;
  message: string;
}

interface TaskResultLike {
  status: number;
  source?: {
    id?: string | number;
    name?: string;
    type?: number;
  };
  errorsList?: string[] | null;
  [key: string]: unknown;
}

interface PayloadDecoder<T> {
  deserializeBinary(bytes: Uint8Array): {
    toObject(): T;
  };
}

function decodePayload<T>(
  decoder: PayloadDecoder<T>,
  payload: BinaryPayloadLike,
): T {
  return decoder.deserializeBinary(payload.value).toObject();
}

/*
 * Return decoded payload through a specified handler for a given type.
 * If a callback handler is not registered in msgHandlers, then messages
 * of that type will be ignored.
 *
 * @param {bytes} msg - Encoded Protobuf message.
 * @param {object} msgHandlers - An object containing optional message
 *   handlers for a given type.
 * @param {function} msgHandlers.message
 * @param {function} msgHandlers.progress
 * @param {function} msgHandlers.taskResult
 * @return {(object|undefined)}
 *   case 1: Registered handler -> message object.
 *   case 2: No handler -> undefined.
 *   case 3: Unknown message type -> undefined.
 */
function payloadRouter<T>(
  msg: Uint8Array,
  msgHandlers: {
    message?: (message: MessageLike) => T;
    progress?: (progress: unknown) => T;
    taskResult?: (taskResult: TaskResultLike) => T;
  },
): T | undefined {
  const envelope = Envelope.deserializeBinary(msg).toObject() as EnvelopeLike;
  const { payload, type } = envelope;

  if (msgHandlers.message && type === PayloadType.MESSAGE) {
    return msgHandlers.message(decodePayload(Message, payload));
  }

  if (msgHandlers.progress && type === PayloadType.PROGRESS) {
    return msgHandlers.progress(decodePayload(Progress, payload));
  }

  if (msgHandlers.taskResult && type === PayloadType.TASK_RESULT) {
    return msgHandlers.taskResult(decodePayload(TaskResult, payload));
  }

  if (!VALID_MESSAGE_TYPES.includes(type)) {
    const serialized = JSON.stringify(envelope);
    const warning = `Unknown payload type ${type}. Discarding. ${serialized}`;
    console.warn(warning);

    captureStack();
    Sentry.captureMessage(warning, Sentry.Severity.Warning);
  }

  return undefined;
}

export { decodePayload, payloadRouter };
